import { clearState } from '@/app/store/clearSlice';
import { getUserIdFromJid } from '@/shared/utils';
import { createSlice } from '@reduxjs/toolkit';

const getUserId = user => getUserIdFromJid(user?.fromUserId);

const initialState = {
   generatedLink: null,
   requestUsers: [],
   requestUsersLoading: false,
   requestUsersClearing: false,
   requestUsersHasMore: true,
   inviteUserCount: 0,
   inviteStatusByUser: {},
   pendingToken: null,
   searchTextByScope: {},
};

const inviteSlice = createSlice({
   name: 'invite',
   initialState,
   reducers: {
      setGeneratedLink: (state, { payload = {} }) => {
         state.generatedLink = payload.inviteLink ?? null;
      },
      setPendingToken: (state, { payload }) => {
         state.pendingToken = payload ?? null;
      },
      setRequestUsers: (state, { payload = {} }) => {
         state.requestUsers = Array.isArray(payload.users) ? payload.users : [];
      },
      setRequestUsersLoading: (state, { payload }) => {
         state.requestUsersLoading = Boolean(payload);
      },
      setRequestUsersClearing: (state, { payload }) => {
         state.requestUsersClearing = Boolean(payload);
      },
      setRequestUsersHasMore: (state, { payload }) => {
         state.requestUsersHasMore = Boolean(payload);
      },
      setInviteUserCount: (state, { payload }) => {
         state.inviteUserCount = Number(payload) || 0;
      },
      appendRequestUsers: (state, { payload = {} }) => {
         const incoming = Array.isArray(payload.users) ? payload.users : [];
         if (payload.prepend) {
            const ids = new Set(incoming.map(getUserId).filter(Boolean));
            state.requestUsers = incoming.concat(state.requestUsers.filter(u => !ids.has(getUserId(u))));
         } else {
            const ids = new Set(state.requestUsers.map(getUserId).filter(Boolean));
            state.requestUsers.push(...incoming.filter(u => (ids.has(getUserId(u)) ? false : ids.add(getUserId(u)))));
         }
      },
      removeAllRequests: state => {
         state.requestUsers = [];
         state.requestUsersHasMore = false;
         state.inviteUserCount = 0;
         state.inviteStatusByUser = {};
      },
      recallMessageInRequestUsers: (state, { payload = {} }) => {
         const { chatId, msgIds } = payload;
         if (!chatId || !Array.isArray(msgIds) || msgIds.length === 0) return;
         const i = state.requestUsers.findIndex(u => getUserId(u) === chatId);
         if (i !== -1 && msgIds.includes(state.requestUsers[i].msgId)) {
            state.requestUsers[i].deleteStatus = 1;
         }
      },
      updateMessageInRequestUsers: (state, { payload = {} }) => {
         const { chatId, msgId, msgBody, editMessageId, editedStatus } = payload;
         if (!chatId) return;
         const i = state.requestUsers.findIndex(u => getUserId(u) === chatId);
         if (i !== -1 && state.requestUsers[i].msgId === msgId) {
            state.requestUsers[i] = {
               ...state.requestUsers[i],
               msgBody: { ...state.requestUsers[i].msgBody, ...msgBody },
               editMessageId,
               editedStatus: editedStatus ?? state.requestUsers[i].editedStatus,
            };
         }
      },
      updateInviteStatus: (state, { payload = {} }) => {
         const { userId, ...patch } = payload;
         if (!userId) return;

         const info = (state.inviteStatusByUser[userId] ??= {});
         for (const k in patch) if (patch[k] !== undefined) info[k] = patch[k];

         if (info.inviteStatus !== 1) {
            const i = state.requestUsers.findIndex(u => getUserId(u) === userId);
            if (i !== -1) {
               state.requestUsers.splice(i, 1);
               state.inviteUserCount = Math.max(0, state.inviteUserCount - 1);
            }
         }
      },
      setSearchTextByScope: (state, { payload = {} }) => {
         const { scope, text } = payload;
         if (!scope) return;
         state.searchTextByScope[scope] = text;
      },
   },
   extraReducers: builder => {
      builder.addCase(clearState, () => initialState);
   },
});

export const {
   setGeneratedLink,
   setPendingToken,
   setRequestUsers,
   setRequestUsersLoading,
   setRequestUsersClearing,
   setRequestUsersHasMore,
   setInviteUserCount,
   appendRequestUsers,
   removeAllRequests,
   recallMessageInRequestUsers,
   updateMessageInRequestUsers,
   updateInviteStatus,
   setSearchTextByScope,
} = inviteSlice.actions;

export const { reducer: inviteReducer } = inviteSlice;
